import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  banWorkspace,
  createWorkspace,
  deleteWorkspace,
  fetchCurrentWorkspace,
  fetchWorkspaces,
  selectWorkspacePath,
  unbanWorkspace,
  updateWorkspace,
  useWorkspace,
} from "../api/workspaceApi";

type CreateWorkspaceInput = {
  path: string;
  name: string;
  description?: string;
  logo?: string;
  cover?: string;
  isPublic?: boolean;
  selected?: boolean;
};

type UpdateWorkspaceInput = {
  id: string;
  path?: string;
  name?: string;
  description?: string;
  logo?: string;
  cover?: string;
  isPublic?: boolean;
  selected?: boolean;
};

export const workspaceKeys = {
  all: ["workspaces"] as const,
  list: ["workspaces", "list"] as const,
  current: ["workspaces", "current"] as const,
};

export const useWorkspacesQuery = (includeDeleted: boolean = false) =>
  useQuery({
    queryKey: [...workspaceKeys.list, includeDeleted],
    queryFn: () => fetchWorkspaces(includeDeleted),
    staleTime: 0,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

export const useCurrentWorkspaceQuery = () =>
  useQuery({
    queryKey: workspaceKeys.current,
    queryFn: fetchCurrentWorkspace,
  });

export const useSelectWorkspacePath = () =>
  useMutation({
    mutationFn: selectWorkspacePath,
  });

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.current });
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateWorkspaceInput) => updateWorkspace(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.current });
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.current });
    },
  });
};

export const useUseWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: useWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.current });
    },
  });
};

export const useSubmitWorkspace = () => {
  const createMutation = useCreateWorkspace();
  return {
    mutateAsync: (input: CreateWorkspaceInput) => createMutation.mutateAsync(input),
    isPending: createMutation.isPending,
  };
};

export const useSelectWorkspace = () => {
  const selectMutation = useSelectWorkspacePath();
  return {
    mutateAsync: () => selectMutation.mutateAsync(),
    isPending: selectMutation.isPending,
  };
};

export const useBanWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => banWorkspace(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
    },
  });
};

export const useUnbanWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unbanWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.list });
    },
  });
};
